/**
 * Jobs API — Supabase PostgREST queries.
 */

import supabase from "../config/supabase";
import notificationsApi from "./notifications";

const JOB_SELECT = `
  *,
  categories:job_categories(category_id, categories(*)),
  locations:job_locations(location_id, locations(*)),
  user:users(id, first_name, last_name, profile_image_url, role, is_placeholder)
`;

const jobsApi = {
  async list(params = {}) {
    let serviceIds = null;

    if (params.location) {
      const names = params.location.split("|");
      const { data: locRows } = await supabase.from("locations").select("id").in("name", names);
      const locIds = (locRows || []).map((l) => l.id);
      if (!locIds.length) return { success: true, data: { items: [], total: 0 } };
      const { data: rows } = await supabase.from("job_locations").select("job_id").in("location_id", locIds);
      serviceIds = [...new Set((rows || []).map((r) => r.job_id))];
      if (!serviceIds.length) return { success: true, data: { items: [], total: 0 } };
    }

    if (params.category) {
      const slugs = params.category.split("|");
      const { data: catRows } = await supabase.from("categories").select("id").in("slug", slugs);
      const catIds = (catRows || []).map((c) => c.id);
      if (!catIds.length) return { success: true, data: { items: [], total: 0 } };
      const { data: rows } = await supabase.from("job_categories").select("job_id").in("category_id", catIds);
      const catIds2 = [...new Set((rows || []).map((r) => r.job_id))];
      if (!catIds2.length) return { success: true, data: { items: [], total: 0 } };
      serviceIds = serviceIds ? serviceIds.filter((id) => catIds2.includes(id)) : catIds2;
      if (!serviceIds.length) return { success: true, data: { items: [], total: 0 } };
    }

    let query = supabase.from("job_postings").select(JOB_SELECT);

    // When listing a specific user's own jobs, show all statuses; otherwise only active
    if (params.user_id) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id === params.user_id) {
        query = query.eq("user_id", params.user_id);
      } else {
        query = query.eq("status", "active").eq("user_id", params.user_id);
      }
    } else {
      query = query.eq("status", "active");
    }
    if (serviceIds) query = query.in("id", serviceIds);
    query = query.order("created_at", { ascending: false });
    if (params.limit) query = query.limit(params.limit);
    if (params.page && params.limit) {
      const offset = (params.page - 1) * params.limit;
      query = query.range(offset, offset + params.limit - 1);
    }

    const { data, error } = await query;
    if (error) return { success: false, error: error.message };
    const items = (data || []).map(flattenJob);
    return { success: true, data: { items, total: serviceIds ? serviceIds.length : items.length } };
  },

  async get(jobId) {
    const { data, error } = await supabase
      .from("job_postings")
      .select(JOB_SELECT)
      .eq("id", jobId)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, data: flattenJob(data) };
  },

  async create(body) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const {
      categories: catSlugsA,
      category_slugs: catSlugsB,
      locations: locNamesA,
      location_names: locNamesB,
      ...jobData
    } = body;

    const catSlugs = catSlugsA || catSlugsB;
    const locNames = locNamesA || locNamesB;

    jobData.user_id = user.id;

    // Admins/moderators skip the queue — go straight to active
    const { data: profile } = await supabase.from("users").select("role").eq("id", user.id).single();
    jobData.status = (profile?.role === "admin" || profile?.role === "moderator") ? "active" : "pending";

    const { data, error } = await supabase.from("job_postings").insert(jobData).select().single();
    if (error) return { success: false, error: error.message };

    if (catSlugs?.length) {
      const { data: cats } = await supabase.from("categories").select("id").in("slug", catSlugs);
      if (cats?.length) {
        await supabase.from("job_categories").insert(
          cats.map((c) => ({ job_id: data.id, category_id: c.id }))
        );
      }
    }

    if (locNames?.length) {
      const { data: locs } = await supabase.from("locations").select("id").in("name", locNames);
      if (locs?.length) {
        await supabase.from("job_locations").insert(
          locs.map((l) => ({ job_id: data.id, location_id: l.id }))
        );
      }
    }

    // Notify admins if status is pending
    if (data.status === "pending") {
      await notificationsApi.notifyAdmins("new_job_ad", "job_posting", data.id);
    }

    return { success: true, data };
  },

  async update(jobId, body) {
    const {
      categories: catSlugsA,
      category_slugs: catSlugsB,
      locations: locNamesA,
      location_names: locNamesB,
      ...rest
    } = body;
    const catSlugs = catSlugsA || catSlugsB;
    const locNames = locNamesA || locNamesB;

    // Only include valid job_postings table fields
    const updateData = {};
    const validFields = ['title', 'description', 'exchange_type', 'status'];
    validFields.forEach(field => {
      if (rest[field] !== undefined) {
        updateData[field] = rest[field];
      }
    });

    const { data, error } = await supabase
      .from("job_postings")
      .update(updateData)
      .eq("id", jobId)
      .select()
      .single();
    if (error) return { success: false, error: error.message };

    // Sync category links (replace set) when provided.
    if (catSlugs) {
      await supabase.from("job_categories").delete().eq("job_id", jobId);
      if (catSlugs.length) {
        const { data: cats } = await supabase.from("categories").select("id").in("slug", catSlugs);
        if (cats?.length) {
          await supabase.from("job_categories").insert(
            cats.map((c) => ({ job_id: jobId, category_id: c.id }))
          );
        }
      }
    }

    // Sync location links (replace set) when provided.
    if (locNames) {
      await supabase.from("job_locations").delete().eq("job_id", jobId);
      if (locNames.length) {
        const { data: locs } = await supabase.from("locations").select("id").in("name", locNames);
        if (locs?.length) {
          await supabase.from("job_locations").insert(
            locs.map((l) => ({ job_id: jobId, location_id: l.id }))
          );
        }
      }
    }

    return { success: true, data };
  },

  async submit(jobId) {
    // Fetch current status first — if an admin created the job it's already
    // 'active' and we must NOT flip it back to 'pending'.
    const { data: current } = await supabase
      .from("job_postings")
      .select("status, title, description")
      .eq("id", jobId)
      .single();

    if (current?.status !== "pending") {
      // Already active (admin post) — nothing to submit.
      return { success: true };
    }

    if (current) {
      supabase.functions.invoke("ai-moderation", {
        body: { item_id: jobId, item_type: "job_posting", title: current.title, description: current.description },
      });
    }

    return { success: true };
  },

  async delete(jobId) {
    const { error } = await supabase.from("job_postings").delete().eq("id", jobId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

function flattenJob(row) {
  if (!row) return row;
  return {
    ...row,
    categories: (row.categories || []).map((jt) => jt.categories).filter(Boolean),
    locations: (row.locations || []).map((jt) => jt.locations).filter(Boolean),
  };
}

export default jobsApi;
